import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
    ChatUiConfiguration,
    ChatUiVariants,
    ContentVariants,
    type Environment as AgenticChatSdkEnvironment,
    listenToChatUi,
    OnBeforeRunConfig,
    updateConfig
} from '@trimble-agentic-external-npm-local/agentic-platform-sdk-iframe-typescript';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ACCESS_TOKEN_KEY } from 'src/app/core/authentication/authentication.service';
import { SidePanelStreamsService } from 'src/app/feature/map-viewer/side-panel/side-panel-streams.service';
import { SidePanelName } from 'src/app/feature/map-viewer/side-panel/side-panel.component';
import { MapWorkspace } from '../../map-data-services/mapWorkspace/map-workspace';
import { CurrentEntitlementStreamService } from '../current-entitlement/current-entitlement-stream.service';
import { EntitlementSKUs } from '../current-entitlement/entitlement';
import { FeaturesStreamsService } from '../current-features/features-streams.service';
import { LayersStreams } from '../current-layers/layers-streams.service';
import { MapWorkspaceStreamsService } from '../current-map-workspaces/map-workspace-streams.service';
import { ProjectStreamService } from '../current-project/project-stream.service';
import { CurrentRegionStreamService } from '../current-region/region-stream.service';
import { environment } from 'src/environments/environment';
import { AiAgentToolsService } from './ai-agent-tools.service';
@Component({
    selector: 'app-ai-chat-interface',
    templateUrl: './ai-chat-interface.component.html',
    styleUrls: ['./ai-chat-interface.component.less']
})
export class AIChatInterfaceComponent implements OnInit, OnDestroy {
    @ViewChild('aiChatFrame')
    set aiChatFrameRef(ref: ElementRef<HTMLIFrameElement> | null) {
        if (!ref) {
            this.teardownChatUi();
            return;
        }
        this.chatFrameRef = ref;
        const iframe = ref.nativeElement;
        const initializeWhenReady = () => {
            setTimeout(() => {
                this.initializeChatUi();
            }, 0);
        };

        try {
            if (iframe.contentDocument?.readyState === 'complete' && iframe.contentWindow) {
                initializeWhenReady();
            } else {
                iframe.addEventListener('load', initializeWhenReady, { once: true });
            }
        } catch (e) {
            iframe.addEventListener('load', initializeWhenReady, { once: true });
        }
    }

    showChatWindow: boolean = false;
    private chatFrameRef?: ElementRef<HTMLIFrameElement>;
    private chatUiUnsubscribe?: () => void;
    private readonly chatUiEnvironment = environment.agenticChatSdkEnvironment as AgenticChatSdkEnvironment;
    readonly chatUiUrl = environment.agenticChatUiUrl;
    readonly safeChatUiUrl: SafeResourceUrl;
    private readonly agentId = environment.agenticChatAgentId;
    private currentWorkspace: MapWorkspace;
    isFeaturePanelOpen: boolean = false;
    featurePanelWidth: number = 0;
    hasAiAssistEntitlement: boolean = false;
    private destroyed = new Subject<void>();

    constructor(
        private sanitizer: DomSanitizer,
        private currentEntitlementStreamService: CurrentEntitlementStreamService,
        private projectStream: ProjectStreamService,
        private regionStream: CurrentRegionStreamService,
        private workspaceStream: MapWorkspaceStreamsService,
        private layersStreams: LayersStreams,
        private sidePanelStreamsService: SidePanelStreamsService,
        private featuresStreams: FeaturesStreamsService,
        private aiAgentTools: AiAgentToolsService
    ) {
        this.safeChatUiUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.chatUiUrl);
        this.workspaceStream.currentMapWorkspaceStream.subscribe((workspace: MapWorkspace) => {
            this.currentWorkspace = workspace;
        });
        this.sidePanelStreamsService.openPanelsStream.pipe(takeUntil(this.destroyed)).subscribe(() => {
            this.isFeaturePanelOpen = this.sidePanelStreamsService.isPanelOpen(SidePanelName.SELECTED_FEATURES);
            this.updateFeaturePanel();
        });
    }

    ngOnInit(): void {
        this.hasAiAssistEntitlement = this.currentEntitlementStreamService.hasEntitlement(
            EntitlementSKUs.MAPVIEWER_AI_ASST
        );
    }

    private updateFeaturePanel(): void {
        if (this.isFeaturePanelOpen) {
            setTimeout(() => {
                const featurePanel = document.querySelector('.selected_features_panel') as HTMLElement;
                if (featurePanel) {
                    this.featurePanelWidth = featurePanel.offsetWidth;
                }
            }, 0);
        } else {
            this.featurePanelWidth = 0;
        }
    }

    toggleChatInterface(): void {
        this.showChatWindow = !this.showChatWindow;
        if (!this.showChatWindow) {
            this.teardownChatUi();
        }
    }

    ngOnDestroy(): void {
        this.teardownChatUi();
        this.destroyed.next();
        this.destroyed.complete();
    }

    private provideToken(): Promise<string> {
        return Promise.resolve(sessionStorage.getItem(ACCESS_TOKEN_KEY) || '');
    }

    private getCurrentRegion(): string {
        return this.regionStream.getRegionInfo().region;
    }

    private getCurrentWorkspace(): string {
        return `${this.currentWorkspace?.id}: ${this.currentWorkspace?.name}`;
    }

    private getCurrentProject(): string {
        const project = this.projectStream.getCurrentProject();
        return project ? `${project.id}: ${project.name}` : '';
    }

    private getCurrentWorkspaceLayers(): string {
        let allLayers = this.layersStreams.currentMapWorkspaceLayers;
        return allLayers.map(layer => `${layer.id}: ${layer.layerName}`).join(', ');
    }

    private getCurrentVisableLayers(): string {
        let allLayers = this.layersStreams.visibleLayersStream.getValue();
        return allLayers.map(layer => `${layer.id}: ${layer.layerName}`).join(', ');
    }

    private getSelectedFeatures(): string {
        const selectedFeatures = this.featuresStreams.selectedFeaturesStream.getValue();
        return selectedFeatures.map(feature => feature.id).join(',');
    }

    private contextBeforeRun = (agentId: string): Promise<OnBeforeRunConfig> => {
        return Promise.resolve({
            tools: {
                runTime: this.aiAgentTools.getRuntimeTools(),
                global: {}
            },
            runContext: {
                context: [
                    {
                        description: 'region',
                        value: this.getCurrentRegion()
                    },
                    {
                        description: 'current_project',
                        value: this.getCurrentProject()
                    },
                    {
                        description: 'current_workspace',
                        value: this.getCurrentWorkspace()
                    },
                    {
                        description: 'current_workspace_layers',
                        value: this.getCurrentWorkspaceLayers()
                    },
                    {
                        description: 'current_visible_layers',
                        value: this.getCurrentVisableLayers()
                    },
                    {
                        description: 'current_selected_feature_ids',
                        value: this.getSelectedFeatures()
                    }
                ]
            }
        });
    };

    private provideConfig(): ChatUiConfiguration {
        return {
            environment: this.chatUiEnvironment,
            uiConfig: {
                theme: 'light',
                contentVariant: ContentVariants.Chat,
                variant: ChatUiVariants.Minimal,
                chatInput: {
                    buttons: [],
                    hideModelSelection: false
                }
            },
            localization: {
                selectedLanguage: 'en'
            },
            agentId: this.agentId
        };
    }

    private initializeChatUi(): void {
        if (!this.chatFrameRef?.nativeElement || this.chatUiUnsubscribe) {
            return;
        }

        this.chatUiUnsubscribe = listenToChatUi(
            this.chatFrameRef.nativeElement,
            this.chatUiUrl,
            () => this.provideConfig(),
            () => this.provideToken(),
            this.contextBeforeRun
        );

        updateConfig(this.chatFrameRef.nativeElement, this.chatUiUrl, this.provideConfig());
    }

    private teardownChatUi(): void {
        if (this.chatUiUnsubscribe) {
            this.chatUiUnsubscribe();
            this.chatUiUnsubscribe = undefined;
        }
        this.chatFrameRef = undefined;
    }
}
